// PrivacyPolicyModal.js
import React from 'react';
import '../Styles/PrivacyPolicyModal.css';

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <h2>Cyphr Systems Privacy Policy</h2>
                <p><strong>Effective Date:</strong> [10/20/2024]</p>
                <p>
                    Cyphr Systems (“we,” “us,” or “our”) values and respects the privacy of our users (“you” or “user”).
                    This Privacy Policy outlines our commitment to data security, privacy-first principles, and
                    transparent handling of any temporary, operational data collected by our apps and services. By using
                    Cyphr Systems applications, you agree to the practices described in this Privacy Policy.
                </p>

                <h3>1. No Data Collection Promise</h3>
                <p>
                    Cyphr Systems is committed to maintaining your privacy. Our services are designed to operate without
                    collecting, storing, or sharing any personal data by default. We operate under a zero data
                    collection policy, meaning no data about you, your device, or your usage patterns is tracked,
                    retained, or shared unless absolutely necessary for temporary operational purposes, as described
                    below.
                </p>

                <h3>2. Temporary Operational Data Collection</h3>
                <p>
                    In certain situations, Cyphr Systems applications may require minimal, temporary data collection to
                    ensure functionality. Such data may include:
                </p>
                <ul>
                    <li><strong>Session Data:</strong> Temporary data collected during active user sessions for the
                        purpose of maintaining service functionality (e.g., active session tracking).
                    </li>
                    <li><strong>Non-Personal Information:</strong> Temporary operational data that does not directly
                        identify you, such as device session identifiers, connection timestamps, or similar
                        non-identifying data necessary for real-time functionality.
                    </li>
                </ul>
                <p>All temporary data collected in these instances is strictly for operational use and is automatically
                    and permanently deleted upon session termination or conclusion of the operational need.</p>

                <h3>3. Data Retention and Deletion</h3>
                <p>Cyphr Systems adheres to a stringent data deletion policy:</p>
                <ul>
                    <li><strong>Automatic Deletion:</strong> Any temporary data collected by Cyphr Systems is retained
                        only for the duration necessary to complete the immediate operational purpose. Once the session
                        ends, or if the data is no longer required, it is permanently deleted from our servers.
                    </li>
                    <li><strong>No Persistent Storage:</strong> Cyphr Systems does not retain any data on our servers
                        after the operational need has ended. We have no access to user data beyond the specific session
                        or feature requiring temporary collection.
                    </li>
                </ul>

                <h3>4. User Agreement on Data Handling</h3>
                <p>
                    By using Cyphr Systems applications, you consent to any temporary data handling strictly for
                    functionality purposes. Users acknowledge and agree that:
                </p>
                <ul>
                    <li>No personal information will be accessed, stored, or retained under any circumstances.</li>
                    <li>Temporary data collection may be required to maintain session functionality, and such data will
                        be deleted promptly when the session or need ends.
                    </li>
                </ul>

                <h3>5. Security Measures</h3>
                <p>
                    Cyphr Systems is committed to protecting the integrity and security of our applications and
                    services:
                </p>
                <ul>
                    <li><strong>Data Encryption:</strong> All temporary data that may be processed during a session is
                        encrypted to ensure its security during transit and processing.
                    </li>
                    <li><strong>Secure Infrastructure:</strong> We utilize secure, industry-standard protocols and
                        infrastructure to protect any temporary data required for operational purposes.
                    </li>
                    <li><strong>Regular Security Audits:</strong> Cyphr Systems continually monitors and improves our
                        security practices to ensure compliance with the highest standards in data privacy and security.
                    </li>
                </ul>

                <h3>6. Third-Party Services</h3>
                <p>
                    Cyphr Systems does not share, sell, or distribute user data to third parties. In cases where
                    external services are integrated (e.g., App Store links), these third-party services may have
                    separate and independent privacy policies. Cyphr Systems is not responsible for the privacy
                    practices of these third parties. We encourage users to review the privacy policies of any
                    third-party services before engaging with them.
                </p>

                <h3>7. Limitation of Liability</h3>
                <p>
                    Cyphr Systems provides all applications and services on an “as-is” basis. While we strive to ensure
                    a secure and seamless experience, we disclaim any liability for potential issues arising from app
                    use, including:
                </p>
                <ul>
                    <li><strong>Data Loss:</strong> Cyphr Systems is not liable for data loss during or after session
                        termination, as all temporary data is designed to be permanently deleted once no longer needed.
                    </li>
                    <li><strong>Unauthorized Access:</strong> While we take robust security measures to prevent
                        unauthorized access, Cyphr Systems is not responsible for damages resulting from potential
                        unauthorized access to your data.
                    </li>
                </ul>

                <h3>8. Prohibited Uses</h3>
                <p>
                    Cyphr Systems is committed to maintaining a safe and secure environment for all users. By using
                    Cyphr Systems applications, you agree not to engage in any prohibited activity, including but not
                    limited to:
                </p>
                <ul>
                    <li><strong>Illegal Activity:</strong> Users may not use Cyphr Systems applications for any
                        unlawful, harmful, or malicious purposes.
                    </li>
                    <li><strong>Harassment or Abuse:</strong> Users agree not to use our applications in a manner that
                        could harm or abuse other users, disrupt our services, or violate any applicable laws.
                    </li>
                    <li><strong>Compliance Responsibility:</strong> Users are responsible for ensuring that their use of
                        Cyphr Systems applications complies with relevant local, state, and federal laws.
                    </li>
                </ul>
                <p>Violation of these terms may result in the suspension or termination of access to Cyphr Systems
                    applications without prior notice.</p>

                <h3>9. Amendments to This Policy</h3>
                <p>
                    Cyphr Systems reserves the right to update, modify, or amend this Privacy Policy at any time to
                    reflect changes in our practices or legal requirements. Any changes to this policy will be posted on
                    our website, and the effective date will be updated. Continued use of Cyphr Systems applications
                    following any changes to this policy constitutes acceptance of the revised terms.
                </p>

                <h3>10. Contact Information</h3>
                <p>
                    If you have any questions or concerns about this Privacy Policy or the data handling practices of
                    Cyphr Systems, please contact us at <a href="mailto:support@cyphr.systems">support@cyphr.systems</a>.
                </p>

                <h3>Acknowledgment</h3>
                <p>
                    By using Cyphr Systems applications, you acknowledge that you have read and understood this Privacy
                    Policy and agree to its terms. Cyphr Systems is committed to providing transparency and protecting
                    your privacy.
                </p>

                <button className="close-button" onClick={onClose}>Close</button>
            </div>
        </div>
    );
};

export default PrivacyPolicyModal;